import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';

export default function ProtocolCreateEditModal({
  Badge,
  Modal,
  Input,
  Dropdown, // Using DS dropdown
  Button,
  IconButton,
  show,
  mode = 'create',
  initialData = null,
  lineOptions = [],
  statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ],
  onClose,
  onSave,
}) {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });
  const [form, setForm] = useState({
    name: '',
    line: '',
    steps: [{ id: 0, name: '' }], // Array de objetos con id y name
    status: 'active',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && initialData) {
      // Mapear steps correctamente, ya sea string[] o {id, name}[]
      let mappedSteps = [{ id: 0, name: '' }];

      if (Array.isArray(initialData?.steps) && initialData.steps.length > 0) {
        mappedSteps = initialData.steps.map((step) => {
          if (typeof step === 'string') {
            return { id: 0, name: step };
          }
          return { id: step.id || 0, name: step.name || step };
        });
      }

      setForm({
        name: initialData?.name ?? '',
        line: initialData?.line ?? '',
        steps: mappedSteps,
        status: initialData?.status ?? 'active',
      });
    } else {
      setForm({
        name: '',
        line: '',
        steps: [{ id: 0, name: '' }],
        status: 'active',
      });
    }
  }, [show, mode, initialData]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateStep = (i, value) => {
    setForm((f) => {
      const steps = [...f.steps];
      steps[i] = { ...steps[i], name: value };
      return { ...f, steps };
    });
  };

  const addStep = () =>
    setForm((f) => ({ ...f, steps: [...f.steps, { id: 0, name: '' }] }));

  const removeStep = (i) =>
    setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));

  const trimmedSteps = useMemo(
    () =>
      form.steps
        .map((s) => ({ id: s.id, name: (s.name ?? '').trim() }))
        .filter((s) => s.name.length > 0),
    [form.steps]
  );

  const isValid = useMemo(() => {
    const hasName = form.name.trim().length >= 3;
    const hasLine = !!form.line;
    const hasSteps = trimmedSteps.length >= 1;
    return hasName && hasLine && hasSteps;
  }, [form.name, form.line, trimmedSteps]);

  const resolvedStatusOptions = useMemo(() => {
    if (Array.isArray(statusOptions) && statusOptions.length > 0) {
      return statusOptions.map((option) => ({
        ...option,
        label:
          option.value === 'active'
            ? t('service_protocol_status_active')
            : option.value === 'inactive'
              ? t('service_protocol_status_inactive')
              : option.label,
      }));
    }
    return [
      {
        label: t('service_protocol_status_active'),
        value: 'active',
      },
      {
        label: t('service_protocol_status_inactive'),
        value: 'inactive',
      },
    ];
  }, [statusOptions, t]);

  const primaryLabel =
    mode === 'edit'
      ? t('service_protocol_modal_save_edit')
      : t('service_protocol_modal_save_create');

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    const payload = {
      id: initialData?.id,
      name: form.name.trim(),
      line: form.line,
      status: form.status,
      steps: trimmedSteps.map((step, idx) => ({
        id: step.id,
        name: step.name,
        order: idx + 1,
      })),
    };

    try {
      await onSave?.(payload);
      setSaving(false);
      onClose?.();
    } catch (e) {
      console.error('Error saving protocol', e);
      setSaving(false);
    }
  };

  if (!Modal || !Input || !Dropdown) return null;

  return (
    <Modal
      title={
        mode === 'edit'
          ? t('service_protocol_modal_edit_title')
          : t('service_protocol_modal_create_title')
      }
      show={show}
      onClose={onClose}
      primaryBtnTxt={primaryLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('service_protocol_modal_cancel')}
      secondaryBtnAction={onClose}
      width={760}
    >
      <div className='grid' style={{ display: 'grid', gap: 16 }}>
        <div
          className='row'
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <Input
            label={t('service_protocol_modal_name_label')}
            placeholder={t('service_protocol_modal_name_placeholder')}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
          />

          <Dropdown
            label={t('service_protocol_modal_line_label')}
            placeholder={t('service_protocol_modal_line_placeholder')}
            options={lineOptions}
            value={lineOptions.find((o) => o.value === form.line) ?? null}
            onChange={(selected) => setField('line', selected?.value ?? '')}
          />
        </div>

        <div>
          <div
            className='d-flex align-items-center justify-content-between'
            style={{ marginBottom: 8 }}
          >
            <span className='label'>{t('service_protocol_modal_steps_label')}</span>
            <Button
              label={t('service_protocol_modal_add_step')}
              iconStart='plus'
              size='sm'
              variant='naked'
              onClick={addStep}
            ></Button>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            {form.steps.map((step, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '56px 1fr 40px',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <Badge text={i + 1} color='light' size='sm' />

                <Input
                  placeholder={t('service_protocol_modal_step_placeholder', {
                    index: i + 1,
                  })}
                  value={step.name}
                  onChange={(e) => updateStep(i, e.target.value)}
                />
                {form.steps.length > 1 ? (
                  IconButton ? (
                    <IconButton
                      ariaLabel={t('service_protocol_modal_remove_step')}
                      icon='trash'
                      onClick={() => removeStep(i)}
                      size='md'
                      iconStroke='var(--error-500)'
                    />
                  ) : (
                    <Button variant='text' onClick={() => removeStep(i)}>
                      {t('service_protocol_modal_remove_step_fallback')}
                    </Button>
                  )
                ) : (
                  <div />
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          <Dropdown
            label={t('service_protocol_modal_status_label')}
            placeholder={t('service_protocol_modal_status_placeholder')}
            options={resolvedStatusOptions}
            value={
              resolvedStatusOptions.find((o) => o.value === form.status) ?? null
            }
            onChange={(selected) =>
              setField('status', selected?.value ?? 'active')
            }
          />
        </div>
      </div>
    </Modal>
  );
}
